import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnonAnalysisView } from "../Components/Analysis/AnonView";
import { getUserName } from "../DataTransform";
import { useQuery } from "@apollo/client";
import { ResultsResult, GET_RESULTS } from "../Queries";
import { ThinLayout } from "../Layouts/ThinLayout";
import { IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { useTranslation } from "react-i18next";

export const ResultsPreview = () => {
  const { campaignId, userId, source } = useParams();
  const { setError } = useErrorBoundary();
  const [t] = useTranslation();

  //
  // State
  //

  const [authorType, setAuthorType] = useState<string>("");

  //
  // Queries
  //

  const { loading: resultsLoading, data: resultsData } =
    useQuery<ResultsResult>(GET_RESULTS, {
      variables: {
        id: campaignId,
        userId,
        authorType: authorType || undefined,
      },
      fetchPolicy: "network-only", // Doesn't check cache before making a network request
      onError: (error) => setError(t("errors.results.list"), error),
    });

  const results = resultsData ? resultsData.results : undefined;

  const showLoading = resultsLoading;

  const campaign = results?.campaign;
  const user = results?.user;
  const analyses = results?.analyses;
  const aggregates = results?.aggregates;
  const overall = results?.overall;
  const template = results?.form;
  const hasMixedAuthors = results?.hasMixedAuthors;
  const hasMultipleRaterTypes = results?.hasMultipleRaterTypes ?? false;
  const hideOverallAverages = campaign?.course?.hideOverallAverages ?? false;

  const title = `${user ? getUserName(user) : "Your"} results for ${
    campaign?.name
  }`;

  // FL-312: this purple full-width secondary bar is the shared preview
  // bar for released results — Work Review's preview renders the same one.
  const secondaryTitle = `Previewing results as ${
    user ? getUserName(user) : ""
  }`;

  const returnUrl =
    source == "campaign"
      ? `/campaigns/${campaignId}`
      : `/results/${campaignId}//${user?.id}/`;

  return (
    <ThinLayout
      title={title}
      loading={showLoading}
      secondaryTitle={secondaryTitle}
      secondaryActionComponent={
        <IconButton
          edge="end"
          aria-label="close"
          component={Link}
          to={returnUrl}
        >
          <Close fontSize={"small"} />
        </IconButton>
      }
    >
      {template && campaign && user && analyses && aggregates && overall && (
        <AnonAnalysisView
          title={title}
          campaign={campaign}
          user={user}
          analyses={analyses}
          aggregates={aggregates}
          overall={overall}
          template={template}
          preview={true}
          hasMixedAuthors={hasMixedAuthors}
          hasMultipleRaterTypes={hasMultipleRaterTypes}
          authorType={authorType}
          onAuthorTypeChange={(v) => setAuthorType(v)}
          hideOverallAverages={hideOverallAverages}
        />
      )}
    </ThinLayout>
  );
};
